import { useEffect, useMemo, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { format } from 'date-fns';
import api from '../api/client';
import { useAuth } from '../context/AuthContext';

export default function Demos() {
  const { usuario } = useAuth();
  const [demos, setDemos] = useState([]);
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState(null);
  const [filtro, setFiltro] = useState('todos'); // todos | activos | por_vencer | vencidos | permanentes
  const [busqueda, setBusqueda] = useState('');

  // Modal extender
  const [demoEdit, setDemoEdit] = useState(null);
  const [formExtender, setFormExtender] = useState({ dias: 7, piscinas: '', usuarios: '' });
  const [guardando, setGuardando] = useState(false);

  // Solo super_admin
  if (usuario && usuario.rol !== 'super_admin') {
    return <Navigate to="/" replace />;
  }

  const cargar = async () => {
    setCargando(true);
    setError(null);
    try {
      const { data } = await api.get('/admin/demos');
      setDemos(Array.isArray(data) ? data : []);
    } catch (e) {
      setError(e.response?.data?.error || e.message);
    } finally {
      setCargando(false);
    }
  };

  useEffect(() => {
    cargar();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ─── KPIs ───
  const kpis = useMemo(() => {
    const activos = demos.filter(
      (d) => d.es_demo && d.dias_restantes != null && d.dias_restantes > 2
    ).length;
    const porVencer = demos.filter(
      (d) => d.es_demo && d.dias_restantes != null && d.dias_restantes > 0 && d.dias_restantes <= 2
    ).length;
    const vencidos = demos.filter(
      (d) => d.es_demo && (d.dias_restantes == null || d.dias_restantes <= 0)
    ).length;
    const total = demos.length;
    return { total, activos, porVencer, vencidos };
  }, [demos]);

  // ─── Filtro + búsqueda ───
  const demosFiltrados = useMemo(() => {
    let lista = [...demos];

    if (filtro === 'activos') {
      lista = lista.filter((d) => d.es_demo && d.dias_restantes != null && d.dias_restantes > 2);
    } else if (filtro === 'por_vencer') {
      lista = lista.filter((d) => d.es_demo && d.dias_restantes != null && d.dias_restantes > 0 && d.dias_restantes <= 2);
    } else if (filtro === 'vencidos') {
      lista = lista.filter((d) => d.es_demo && (d.dias_restantes == null || d.dias_restantes <= 0));
    } else if (filtro === 'permanentes') {
      lista = lista.filter((d) => !d.es_demo);
    }

    if (busqueda.trim()) {
      const q = busqueda.toLowerCase();
      lista = lista.filter(
        (d) =>
          d.nombre?.toLowerCase().includes(q) ||
          d.email?.toLowerCase().includes(q) ||
          d.finca_nombre?.toLowerCase().includes(q)
      );
    }

    return lista;
  }, [demos, filtro, busqueda]);

  // ─── Acciones ───
  const abrirExtender = (demo) => {
    setDemoEdit(demo);
    setFormExtender({
      dias: 7,
      piscinas: demo.limite_piscinas ?? '',
      usuarios: demo.limite_usuarios ?? '',
    });
  };

  const guardarExtender = async () => {
    if (!demoEdit) return;
    setGuardando(true);
    try {
      const payload = {};
      if (formExtender.dias) payload.dias = parseInt(formExtender.dias, 10);
      if (formExtender.piscinas) payload.piscinas = parseInt(formExtender.piscinas, 10);
      if (formExtender.usuarios) payload.usuarios = parseInt(formExtender.usuarios, 10);

      await api.put(`/admin/demos/${demoEdit.id}/extender`, payload);
      setDemoEdit(null);
      await cargar();
    } catch (e) {
      alert(e.response?.data?.error || 'Error al extender la licencia');
    } finally {
      setGuardando(false);
    }
  };

  const hacerPermanente = async (demo) => {
    const ok = confirm(
      `¿Convertir a PERMANENTE la cuenta de ${demo.nombre}?\n\n` +
      `Se eliminarán los límites y la fecha de expiración.\n` +
      `Esta acción no se puede deshacer.`
    );
    if (!ok) return;
    try {
      await api.put(`/admin/demos/${demo.id}/permanente`);
      await cargar();
    } catch (e) {
      alert(e.response?.data?.error || 'Error al convertir a permanente');
    }
  };

  const eliminar = async (demo) => {
    const ok = confirm(
      `¿Eliminar la cuenta demo de ${demo.nombre}?\n\n` +
      `Se borrarán la finca, sector, piscinas y todos sus datos.\n\n` +
      `Esta acción NO se puede deshacer.`
    );
    if (!ok) return;
    try {
      await api.delete(`/admin/demos/${demo.id}`);
      await cargar();
    } catch (e) {
      alert(e.response?.data?.error || 'Error al eliminar');
    }
  };

  // ─── Estado visual de cada demo ───
  const estadoBadge = (d) => {
    if (!d.es_demo) {
      return <span className="inline-block px-2 py-0.5 rounded text-[11px] font-semibold bg-green-100 text-green-800">PERMANENTE</span>;
    }
    if (d.dias_restantes == null || d.dias_restantes <= 0) {
      return <span className="inline-block px-2 py-0.5 rounded text-[11px] font-semibold bg-red-100 text-red-800">VENCIDO</span>;
    }
    if (d.dias_restantes <= 2) {
      return <span className="inline-block px-2 py-0.5 rounded text-[11px] font-semibold bg-amber-100 text-amber-800">POR VENCER</span>;
    }
    return <span className="inline-block px-2 py-0.5 rounded text-[11px] font-semibold bg-blue-100 text-blue-800">ACTIVO</span>;
  };

  return (
    <div className="p-6">
      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-3xl font-bold text-slate-700">Cuentas Demo</h1>
          <p className="text-sm text-gray-500 mt-1">
            Gestiona licencias de prueba: extiende días, ajusta límites o convierte a permanente.
          </p>
        </div>
        <button
          onClick={cargar}
          className="px-4 py-2 border border-slate-400 text-slate-700 rounded-lg hover:bg-slate-100 text-sm font-medium"
        >
          🔄 Actualizar
        </button>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <button
          onClick={() => setFiltro('todos')}
          className={`text-left bg-white p-4 rounded-xl shadow-sm border-2 transition hover:shadow-md ${filtro === 'todos' ? 'border-slate-600' : 'border-transparent'}`}
        >
          <div className="text-xs text-gray-500">Total</div>
          <div className="text-3xl font-bold text-slate-700">{kpis.total}</div>
        </button>
        <button
          onClick={() => setFiltro('activos')}
          className={`text-left bg-white p-4 rounded-xl shadow-sm border-2 transition hover:shadow-md ${filtro === 'activos' ? 'border-blue-500' : 'border-transparent'}`}
        >
          <div className="text-xs text-gray-500">Activos</div>
          <div className="text-3xl font-bold text-blue-600">{kpis.activos}</div>
        </button>
        <button
          onClick={() => setFiltro('por_vencer')}
          className={`text-left bg-white p-4 rounded-xl shadow-sm border-2 transition hover:shadow-md ${filtro === 'por_vencer' ? 'border-amber-500' : 'border-transparent'}`}
        >
          <div className="text-xs text-gray-500">Por vencer (≤2 días)</div>
          <div className="text-3xl font-bold text-amber-600">{kpis.porVencer}</div>
        </button>
        <button
          onClick={() => setFiltro('vencidos')}
          className={`text-left bg-white p-4 rounded-xl shadow-sm border-2 transition hover:shadow-md ${filtro === 'vencidos' ? 'border-red-500' : 'border-transparent'}`}
        >
          <div className="text-xs text-gray-500">Vencidos</div>
          <div className="text-3xl font-bold text-red-600">{kpis.vencidos}</div>
        </button>
      </div>

      {/* Filtros */}
      <div className="bg-white rounded-xl shadow-sm p-4 mb-6 flex flex-wrap items-center gap-3">
        <input
          type="text"
          value={busqueda}
          onChange={(e) => setBusqueda(e.target.value)}
          placeholder="🔍 Buscar por nombre, email o finca..."
          className="flex-1 min-w-[240px] px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-slate-400"
        />
        <select
          value={filtro}
          onChange={(e) => setFiltro(e.target.value)}
          className="px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-slate-400"
        >
          <option value="todos">Todos</option>
          <option value="activos">Solo activos</option>
          <option value="por_vencer">Por vencer</option>
          <option value="vencidos">Vencidos</option>
          <option value="permanentes">Permanentes</option>
        </select>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 p-3 rounded-lg mb-4">
          {error}
        </div>
      )}

      {/* Tabla */}
      <div className="bg-white rounded-xl shadow-sm overflow-hidden">
        {cargando ? (
          <div className="p-12 text-center text-gray-500">Cargando...</div>
        ) : demosFiltrados.length === 0 ? (
          <div className="p-12 text-center text-gray-500">
            {demos.length === 0
              ? 'Aún no hay cuentas demo registradas.'
              : 'No hay cuentas con ese filtro.'}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-100 text-slate-700">
                <tr>
                  <th className="text-left px-4 py-3 font-semibold">Usuario</th>
                  <th className="text-left px-4 py-3 font-semibold">Finca</th>
                  <th className="text-center px-4 py-3 font-semibold">Estado</th>
                  <th className="text-center px-4 py-3 font-semibold">Días rest.</th>
                  <th className="text-center px-4 py-3 font-semibold">Piscinas</th>
                  <th className="text-center px-4 py-3 font-semibold">Usuarios</th>
                  <th className="text-left px-4 py-3 font-semibold">Creado</th>
                  <th className="text-right px-4 py-3 font-semibold">Acciones</th>
                </tr>
              </thead>
              <tbody>
                {demosFiltrados.map((d) => {
                  const piscinasUso = d.piscinas_actuales ?? 0;
                  const piscinasMax = d.limite_piscinas ?? '∞';
                  const usuariosUso = d.usuarios_actuales ?? 0;
                  const usuariosMax = d.limite_usuarios ?? '∞';

                  return (
                    <tr key={d.id} className="border-t hover:bg-slate-50">
                      <td className="px-4 py-3">
                        <div className="font-semibold text-slate-800">{d.nombre}</div>
                        <div className="text-xs text-gray-500">{d.email}</div>
                        {d.telefono && (
                          <div className="text-xs text-gray-400">📞 {d.telefono}</div>
                        )}
                      </td>
                      <td className="px-4 py-3 text-gray-700 text-xs">
                        {d.finca_nombre || '—'}
                      </td>
                      <td className="px-4 py-3 text-center">{estadoBadge(d)}</td>
                      <td className="px-4 py-3 text-center font-mono">
                        {d.es_demo
                          ? d.dias_restantes != null && d.dias_restantes > 0
                            ? `${d.dias_restantes} d`
                            : '0 d'
                          : '∞'}
                      </td>
                      <td className="px-4 py-3 text-center">
                        <span className="font-semibold">{piscinasUso}</span>
                        <span className="text-gray-400"> / {piscinasMax}</span>
                      </td>
                      <td className="px-4 py-3 text-center">
                        <span className="font-semibold">{usuariosUso}</span>
                        <span className="text-gray-400"> / {usuariosMax}</span>
                      </td>
                      <td className="px-4 py-3 text-xs text-gray-500">
                        {d.creado_en ? format(new Date(d.creado_en), 'dd/MM/yyyy') : '—'}
                      </td>
                      <td className="px-4 py-3 text-right whitespace-nowrap">
                        {d.es_demo ? (
                          <>
                            <button
                              onClick={() => abrirExtender(d)}
                              className="text-xs px-2 py-1 text-slate-700 border border-slate-300 rounded hover:bg-slate-100 mr-1"
                              title="Extender días y ajustar límites"
                            >
                              ⏱ Extender
                            </button>
                            <button
                              onClick={() => hacerPermanente(d)}
                              className="text-xs px-2 py-1 text-green-700 border border-green-300 rounded hover:bg-green-50 mr-1"
                              title="Convertir a permanente"
                            >
                              ✓ Permanente
                            </button>
                            <button
                              onClick={() => eliminar(d)}
                              className="text-xs px-2 py-1 text-red-600 border border-red-300 rounded hover:bg-red-50"
                              title="Eliminar cuenta demo"
                            >
                              🗑
                            </button>
                          </>
                        ) : (
                          <span className="text-xs text-gray-400 italic">Sin acciones</span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* ─── Modal extender ─── */}
      {demoEdit && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60"
          onClick={() => !guardando && setDemoEdit(null)}
        >
          <div
            className="bg-white rounded-2xl w-full max-w-md shadow-2xl overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="px-6 py-4 bg-slate-700 text-white flex justify-between items-center">
              <div>
                <h2 className="text-base font-bold">Extender licencia</h2>
                <p className="text-xs text-slate-300">{demoEdit.nombre}</p>
              </div>
              <button
                onClick={() => !guardando && setDemoEdit(null)}
                className="w-8 h-8 rounded-full hover:bg-white/10 text-sm"
              >
                ✕
              </button>
            </div>

            <div className="p-6 space-y-4">
              <div className="bg-slate-50 rounded-lg p-3 text-xs text-slate-600">
                <div className="flex justify-between">
                  <span>Estado actual:</span>
                  <span className="font-semibold">
                    {demoEdit.dias_restantes != null && demoEdit.dias_restantes > 0
                      ? `${demoEdit.dias_restantes} días restantes`
                      : 'Vencido'}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span>Expira:</span>
                  <span className="font-mono text-[11px]">
                    {demoEdit.fecha_expiracion_demo
                      ? format(new Date(demoEdit.fecha_expiracion_demo), 'dd/MM/yyyy HH:mm')
                      : '—'}
                  </span>
                </div>
              </div>

              <label className="block">
                <span className="text-xs font-medium text-slate-700">
                  Días a agregar
                </span>
                <input
                  type="number"
                  min="1"
                  value={formExtender.dias}
                  onChange={(e) =>
                    setFormExtender((f) => ({ ...f, dias: e.target.value }))
                  }
                  className="mt-1 w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-slate-400"
                />
                <span className="text-[11px] text-gray-500">
                  Si la licencia ya venció, la nueva fecha se calcula desde hoy.
                </span>
              </label>

              <div className="grid grid-cols-2 gap-3">
                <label className="block">
                  <span className="text-xs font-medium text-slate-700">
                    Límite piscinas
                  </span>
                  <input
                    type="number"
                    min="1"
                    value={formExtender.piscinas}
                    onChange={(e) =>
                      setFormExtender((f) => ({ ...f, piscinas: e.target.value }))
                    }
                    className="mt-1 w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-slate-400"
                    placeholder="Ej: 5"
                  />
                </label>
                <label className="block">
                  <span className="text-xs font-medium text-slate-700">
                    Límite usuarios
                  </span>
                  <input
                    type="number"
                    min="1"
                    value={formExtender.usuarios}
                    onChange={(e) =>
                      setFormExtender((f) => ({ ...f, usuarios: e.target.value }))
                    }
                    className="mt-1 w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-slate-400"
                    placeholder="Ej: 4"
                  />
                </label>
              </div>

              <div className="flex justify-end gap-2 pt-2">
                <button
                  onClick={() => !guardando && setDemoEdit(null)}
                  disabled={guardando}
                  className="px-4 py-2 border rounded-lg text-sm hover:bg-gray-100"
                >
                  Cancelar
                </button>
                <button
                  onClick={guardarExtender}
                  disabled={guardando}
                  className="px-4 py-2 bg-slate-700 text-white rounded-lg text-sm font-medium hover:bg-slate-800 disabled:opacity-50"
                >
                  {guardando ? 'Guardando...' : 'Extender'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}